import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { accessControlAction } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { useDeviceStore } from '@/stores/deviceStore';
import type { AccessKey, GenerateKeyResponse } from '@/types';

export function useKeys() {
  const { user } = useAuth();
  const { activeDeviceId } = useDeviceStore();

  return useQuery({
    queryKey: ['keys', user?.id, activeDeviceId],
    queryFn: async (): Promise<AccessKey[]> => {
      if (!user || !activeDeviceId) return [];

      const { data, error } = await supabase
        .from('keys_v1')
        .select('*')
        .eq('user_id', user.id)
        .eq('device_id', activeDeviceId)
        .eq('status', 'active')
        .gte('expires_at', new Date().toISOString())
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      return (data ?? []) as AccessKey[];
    },
    enabled: !!user && !!activeDeviceId,
    refetchInterval: 30_000,
  });
}

/** Fetch all active keys across all devices for the current user. */
export function useAllKeys() {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['all-keys', user?.id],
    queryFn: async (): Promise<AccessKey[]> => {
      if (!user) return [];

      const { data, error } = await supabase
        .from('keys_v1')
        .select('*')
        .eq('user_id', user.id)
        .eq('status', 'active')
        .gte('expires_at', new Date().toISOString())
        .order('device_id')
        .order('created_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      return (data ?? []) as AccessKey[];
    },
    enabled: !!user,
    refetchInterval: 30_000,
  });
}

export function useRevokeKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const { activeDeviceId } = useDeviceStore();

  return useMutation({
    mutationFn: async (keyId: string) => {
      const result = await accessControlAction<{ success: boolean }>(
        'revoke-my-key',
        { key_id: keyId }
      );
      if (result.error) throw new Error(result.error);
      return result.data;
    },
    onMutate: async (keyId) => {
      const queryKey = ['keys', user?.id, activeDeviceId];
      const allKeysKey = ['all-keys', user?.id];
      await queryClient.cancelQueries({ queryKey });
      await queryClient.cancelQueries({ queryKey: allKeysKey });
      const previous = queryClient.getQueryData<AccessKey[]>(queryKey);
      const previousAll = queryClient.getQueryData<AccessKey[]>(allKeysKey);

      queryClient.setQueryData<AccessKey[]>(
        queryKey,
        (old) => old?.filter((k) => k.id !== keyId) ?? []
      );
      queryClient.setQueryData<AccessKey[]>(
        allKeysKey,
        (old) => old?.filter((k) => k.id !== keyId) ?? []
      );

      return { previous, previousAll };
    },
    onError: (_err, _id, context) => {
      if (context?.previous) {
        queryClient.setQueryData(['keys', user?.id, activeDeviceId], context.previous);
      }
      if (context?.previousAll) {
        queryClient.setQueryData(['all-keys', user?.id], context.previousAll);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['keys', user?.id, activeDeviceId] });
      queryClient.invalidateQueries({ queryKey: ['all-keys', user?.id] });
    },
  });
}

export function useGenerateKey() {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async (params: {
      device_id: string;
      port_code?: string;
      access_method?: 'qr' | 'nfc' | 'both';
      duration_seconds?: number;
      payment_id?: string;
      booking_id?: string;
      verification_token?: string;
    }): Promise<GenerateKeyResponse> => {
      if (!params.device_id) throw new Error('Ingen enhet vald');

      const result = await accessControlAction<GenerateKeyResponse>('generate-key', {
        device_id: params.device_id,
        port_code: params.port_code ?? 'p1',
        access_method: params.access_method ?? 'both',
        ...(params.duration_seconds && { duration_seconds: params.duration_seconds }),
        ...(params.payment_id && { payment_id: params.payment_id }),
        ...(params.booking_id && { booking_id: params.booking_id }),
        ...(params.verification_token && { verification_token: params.verification_token }),
      });

      if (result.error) throw new Error(result.error);
      if (!result.data) throw new Error('Ingen nyckel genererad');
      return result.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['all-keys', user?.id] });
      queryClient.invalidateQueries({ queryKey: ['keys', user?.id] });
    },
  });
}
